// Local
import { Analytics, AnalyticsProps } from '@northerntrust-internal/uxp-analytics';
import { OriginMapping, ReferenceData } from './types';

export const appAnalytics: Analytics = new Analytics({ segmentKey: process.env.UXP_SEGMENT_API_KEY });

/**
 * @param {Array<string>} items
 * @param {string} item
 * @return {Array<string>}
 */
export const addAndRemoveItemFromArray = (items: string[], item: string): Array<string> => {
  // reference the items
  let referenceLooseItems: string[] = [];
  if (items) {
    referenceLooseItems = [...items];
    // get the index of items
    const itemIndex = referenceLooseItems?.indexOf(item);
    // check index
    if (itemIndex === -1) {
      referenceLooseItems.push(item);
    } else {
      referenceLooseItems.splice(itemIndex, 1);
    }
  } else {
    referenceLooseItems.push(item);
  }
  return referenceLooseItems;
};

/**
 * @return {string}
 */
export const uniqueID = (): string => crypto.randomUUID();

const dataGridLicenseKey = 'Using_this_{AG_Grid}_Enterprise_key_{AG-090513}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Northern_Trust}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{AOR}_only_for_{5}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{AOR}_need_to_be_licensed___{AOR}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{31_December_2026}____[v3]_[01]_MTc5ODY3NTIwMDAwMA==7767d6f5146b49e2c12a2d2b6e22c724';

const makeLegalEntityToggle = (showDefaultEntity: boolean) => ({
  corporation: {
    showAssetManager: false,
    showDefaultEntity,
    defaultEntityAPI: false,
    taxProfileAPI: false
  },
  individual: {
    showAssetManager: false,
    showDefaultEntity,
    defaultEntityAPI: false,
    taxProfileAPI: false
  },
  trust: {
    showAssetManager: false,
    showDefaultEntity,
    defaultEntityAPI: false,
    taxProfileAPI: false
  }
});

const makeOriginConfig = ({
  okta,
  env,
  dcoUrl,
  pingUrl,
  dynatraceId,
  showDefaultEntity,
  apiBaseUrl
}: {
  okta: string;
  env: string;
  dcoUrl: string;
  pingUrl?: string;
  dynatraceId: string;
  showDefaultEntity: boolean;
  apiBaseUrl: string;
}) => ({
  okta,
  env,
  dcoUrl,
  ...(pingUrl ? { pingUrl } : {}),
  dynatraceId,
  dataGridLicenseKey,
  legalEntityToggle: makeLegalEntityToggle(showDefaultEntity),
  apiBaseUrl
});

export const ORIGIN_MAPPING: OriginMapping = {
  'localhost.ntrs.com:8080': makeOriginConfig({
    okta: 'local',
    env: 'DEV',
    dcoUrl: 'https://int.doccollab.ntrs.com',
    dynatraceId: '178881c0ab8/bf17498jmb/1a0dfbcfbd2caea',
    showDefaultEntity: true,
    apiBaseUrl: 'https://api-dev.northerntrust.com/aor/account-opening'
  }),
  'dev.accountopen.ntrs.com': makeOriginConfig({
    okta: 'dev',
    env: 'DEV',
    dcoUrl: 'https://dev.doccollab.ntrs.com',
    dynatraceId: '178881c0ab8/bf17498jmb/1a0dfbcfbd2caea',
    showDefaultEntity: true,
    apiBaseUrl: 'https://api-dev.northerntrust.com/aor/account-opening'
  }),
  'int.accountopen.ntrs.com': makeOriginConfig({
    okta: 'int',
    env: 'INT',
    dcoUrl: 'https://int.doccollab.ntrs.com',
    dynatraceId: '178881c0ab8/bf17498jmb/4ca4795eca5d9e5e',
    showDefaultEntity: true,
    apiBaseUrl: 'https://api-int.northerntrust.com/aor/account-opening'
  }),
  'uat.accountopen.ntrs.com': makeOriginConfig({
    okta: 'uat',
    env: 'UAT',
    dcoUrl: 'https://uat.doccollab.ntrs.com',
    pingUrl: 'https://nam-a.ntrs.com/wmc/web/header/sessionRefresh',
    dynatraceId: '178881c0ab8/bf17498jmb/2faa1874c88436cb',
    showDefaultEntity: false,
    apiBaseUrl: 'https://api-uat.northerntrust.com/aor/account-opening-uat'
  }),
  'uat2.accountopen.ntrs.com': makeOriginConfig({
    okta: 'uat2',
    env: 'UAT2',
    dcoUrl: 'https://uat.doccollab.ntrs.com',
    pingUrl: 'https://nam-a.ntrs.com/wmc/web/header/sessionRefresh',
    dynatraceId: '178881c0ab8/bf17498jmb/2a3bb10589a5d470',
    showDefaultEntity: false,
    apiBaseUrl: 'https://api-uat.northerntrust.com/aor/account-opening-uat2'
  }),
  'accountopen.ntrs.com': makeOriginConfig({
    okta: 'prod',
    env: 'PROD',
    dcoUrl: 'https://doccollab.ntrs.com',
    pingUrl: 'https://wwwc.ntrs.com/wmc/web/header/sessionRefresh',
    dynatraceId: '157944990f8/bf57712wec/ae1237f71ec3378a',
    showDefaultEntity: false,
    apiBaseUrl: 'https://api.northerntrust.com/aor/account-opening'
  })
};

export const DEFAULT_ORIGIN_MAP = Object.keys(ORIGIN_MAPPING).reduce(
  (acc, origin) => {
    acc[origin] = ORIGIN_MAPPING[origin].okta;
    return acc;
  },
  {} as Record<string, string>
);

export const getLastOrigin = (originMap: object, href: string): string => {
  const keyList = Object.keys(originMap);
  let key: string = 'localhost.ntrs.com:8080';
  for (const element of keyList) {
    if (href.includes(element)) {
      key = element;
      break;
    }
  }
  return key;
};

export const getEnv = (customOriginMap: any) => {
  const originMap = customOriginMap || {};
  const finalOriginMap = {
    ...DEFAULT_ORIGIN_MAP,
    ...originMap
  };
  const last = getLastOrigin(finalOriginMap, window.location.href);
  return finalOriginMap[last];
};

export const getOIDCConfiguration = (OIDCConfigs: any, customOriginMap: any) => {
  const sharedConfigs = OIDCConfigs.shared;
  let OIDCConfig = sharedConfigs || {};
  const envConfig = OIDCConfigs[getEnv(customOriginMap)];
  if (envConfig) {
    OIDCConfig = { ...OIDCConfig, ...envConfig };
  } else {
    OIDCConfig = { ...OIDCConfig, ...OIDCConfigs.default };
  }
  return OIDCConfig;
};

export const last = getLastOrigin(ORIGIN_MAPPING, window.location.href);

export const API_BASE_URL = ORIGIN_MAPPING[last].apiBaseUrl;

const ACCOUNT_OPENING_EXPERIENCE = 'Account Opening';

const getAccountOpeningEnv = () =>
  `${ACCOUNT_OPENING_EXPERIENCE} - ${ORIGIN_MAPPING[last].env}`;

const getCommonAnalyticsIds = (pageObject: AnalyticsProps, personaId: number, sponsorCode: string) => ({
  cd52_sponsor_code: sponsorCode,
  cd53_user_id_hit_scope: `${personaId}`,
  cd54_user_id_user_scope: `${personaId}`,
  cd98_page_name: pageObject?.pageName
});

export const triggerAnalytics = (pageObject: AnalyticsProps, personaId: number, sponsorCode: string): void => {
  const accountOpeningEnv = getAccountOpeningEnv();
  const pageProps = {
    env: accountOpeningEnv,
    experience: ACCOUNT_OPENING_EXPERIENCE,
    ...pageObject,
    properties: {
      environment: accountOpeningEnv,
      experience: ACCOUNT_OPENING_EXPERIENCE,
      ...getCommonAnalyticsIds(pageObject, personaId, sponsorCode)
    } as any
  };
  appAnalytics.page({ ...pageProps });
};

export const eventTriggerAnalytics = ({
  category,
  eventTrack,
  eventTitle,
  pageObject,
  personaId,
  sponsorCode,
  draftRandomId = '',
  accountOpenToSubmitTimeDiff = 0,
  eventAction = 'click'
}: {
  category: string;
  eventTrack: string;
  eventTitle: string;
  pageObject: AnalyticsProps;
  personaId: number;
  sponsorCode: string;
  draftRandomId?: string;
  accountOpenToSubmitTimeDiff?: number;
  eventAction?: string;
}): void => {
  const accountOpeningEnv = getAccountOpeningEnv();
  const eventProps = {
    env: accountOpeningEnv,
    experience: ACCOUNT_OPENING_EXPERIENCE,
    ...pageObject,
    properties: {
      eventAction,
      eventCategory: category,
      eventLabel: eventTitle,
      environment: accountOpeningEnv,
      experience: ACCOUNT_OPENING_EXPERIENCE,
      cd100_event_title: eventTitle,
      ...getCommonAnalyticsIds(pageObject, personaId, sponsorCode),
      cd102_draft_id: draftRandomId,
      cd101_elapsed_duration: accountOpenToSubmitTimeDiff
    }
  };
  appAnalytics.track(eventTrack, { ...eventProps });
};

export const mapItems = (
  items: string[] = [],
  referenceList: ReferenceData[] = [],
  key: 'label' | 'value' = 'label',
  target: 'label' | 'value' = 'value'
): string[] => {
  return (
    items?.map((item: string) => {
      return referenceList?.find(ref => ref?.[key] === item)?.[target] ?? '';
    }) ?? []
  );
};

export const yesNoToBoolean = (value?: string): boolean | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return value.toLowerCase() === 'yes';
};

export const booleanToYesNo = (value?: boolean): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return value ? 'Yes' : 'No';
};

export const getDataGridLicenseKey = (): string => {
  return ORIGIN_MAPPING[last].dataGridLicenseKey;
};

export const defaultColDef = {
  flex: 1,
  minWidth: 80,
  suppressHeaderMenuButton: true,
};
